import {
  addDays,
  differenceInCalendarDays,
  format,
  format as formatDateFns,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import DatePicker from 'components/datepicker';
import Icon from 'components/icons';
import { Spinner } from 'components/loadings';
import moment from 'moment';

import { computeDeadlineWeeksToComplete } from './deadlineWeeks';
import {
  CustomDateExpanded,
  CustomDateHeader,
  CustomDateMeta,
  CustomDateSummary,
  DeadlineResultBox,
  DeadlineResultDate,
  DeadlineResultMeta,
  InlineSpinnerContainer,
  OptionCard,
  OptionLabel,
  OptionSubtitle,
  OptionTextBlock,
  OptionTopRow,
  OptionsList,
  PaceButton,
  PaceButtonsCol,
  PaceLabel,
  PaceUnit,
  PaceValue,
  PastDateWarning,
  RadioDot,
  RecommendedExpanded,
  RecommendedLoadingNotice,
  StyledDatePicker,
  TrainingPaceControl,
  TrainingPacePill,
} from './styles';

export interface DeadlineSelectorValue {
  type: 'none' | 'recommended' | 'custom';
  date: string | null;
  trainingPaceHoursPerWeek?: number;
}

export interface DeadlineSelectorProps {
  value: DeadlineSelectorValue;
  onChange: (val: DeadlineSelectorValue) => void;
  playlistDurationSeconds?: number;
  userProgress?: number;
  hideRecommended?: boolean;
  disableRecommended?: boolean;
  isBulk?: boolean;
  selectedUsersCount?: number;
  hideCustom?: boolean;
  recommendedPreviewMode?: 'date' | 'weeks';
}

export const DEFAULT_TRAINING_PACE = 4;
const MIN_PACE = 1;
const MAX_PACE = 40;

const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return `${h}h ${m}m`;
};

const computeRecommendedDate = (remainingSeconds: number, hoursPerWeek: number): string => {
  const remainingHours = remainingSeconds / 3600;
  const daysNeeded = Math.max(1, Math.ceil((remainingHours / hoursPerWeek) * 7));

  return format(addDays(new Date(), daysNeeded), 'yyyy-MM-dd');
};

const isPastDate = (date: string | null): boolean => {
  if (!date) return false;

  return moment(date).isBefore(moment(), 'day');
};

const disablePastDates = (date: Date): boolean =>
  isBefore(startOfDay(date), startOfDay(new Date()));

const getNormalizedDate = (
  date: Date | string | null,
  dateString?: string | string[]
): string | null => {
  if (date instanceof Date && isValid(date)) {
    return formatDateFns(date, 'yyyy-MM-dd');
  }

  if (typeof date === 'string' && date) {
    const parsedDate = parseISO(date);

    if (isValid(parsedDate)) {
      return formatDateFns(parsedDate, 'yyyy-MM-dd');
    }
  }

  if (typeof dateString === 'string' && dateString) {
    const parsedDate = parseISO(dateString);

    if (isValid(parsedDate)) {
      return formatDateFns(parsedDate, 'yyyy-MM-dd');
    }
  }

  return null;
};

const formatHoursPerWeek = (hours: number): string => {
  const roundedHours = Math.round(hours * 10) / 10;

  return Number.isInteger(roundedHours) ? roundedHours.toFixed(0) : roundedHours.toFixed(1);
};

const getCustomTrainingPaceNeeded = (
  remainingSeconds: number,
  targetDate: string | null
): number | null => {
  if (!targetDate || remainingSeconds <= 0) return null;

  const daysUntilDeadline = differenceInCalendarDays(parseISO(targetDate), startOfDay(new Date()));

  if (daysUntilDeadline <= 0) return null;

  const weeksUntilDeadline = daysUntilDeadline / 7;

  if (weeksUntilDeadline <= 0) return null;

  return remainingSeconds / 3600 / weeksUntilDeadline;
};

const DeadlineSelector = ({
  value,
  onChange,
  playlistDurationSeconds = 0,
  userProgress = 0,
  hideRecommended = false,
  disableRecommended = false,
  isBulk = false,
  selectedUsersCount = 0,
  hideCustom = false,
  recommendedPreviewMode = 'date',
}: DeadlineSelectorProps): JSX.Element => {
  const shouldShowBulkRecommendedSubtitle = isBulk && selectedUsersCount > 1;
  const remainingSeconds = Math.max(0, playlistDurationSeconds * (1 - userProgress));
  const pace = value.trainingPaceHoursPerWeek ?? DEFAULT_TRAINING_PACE;
  const weeksToComplete = computeDeadlineWeeksToComplete(playlistDurationSeconds, pace);
  const recommendedDate =
    remainingSeconds > 0
      ? computeRecommendedDate(remainingSeconds, pace)
      : format(new Date(), 'yyyy-MM-dd');
  const recommendedDaysNeeded =
    remainingSeconds > 0 ? Math.max(1, Math.ceil((remainingSeconds / 3600 / pace) * 7)) : 0;
  const customTrainingPaceNeeded = getCustomTrainingPaceNeeded(remainingSeconds, value.date);

  const handleSelect = (type: DeadlineSelectorValue['type']) => {
    if (type === 'none') {
      onChange({ type: 'none', date: null });
      return;
    }

    if (type === 'recommended') {
      if (disableRecommended) {
        return;
      }
      onChange({
        type: 'recommended',
        date: recommendedDate,
        trainingPaceHoursPerWeek: pace,
      });
      return;
    }

    onChange({ type: 'custom', date: value.type === 'custom' ? value.date : null });
  };

  const handlePaceChange = (delta: number) => {
    const nextPace = Math.min(MAX_PACE, Math.max(MIN_PACE, pace + delta));
    const nextDate =
      remainingSeconds > 0
        ? computeRecommendedDate(remainingSeconds, nextPace)
        : format(new Date(), 'yyyy-MM-dd');

    onChange({
      type: 'recommended',
      date: nextDate,
      trainingPaceHoursPerWeek: nextPace,
    });
  };

  const handleDateChange = (date: Date | string | null, dateString?: string | string[]) => {
    onChange({
      type: 'custom',
      date: getNormalizedDate(date, dateString),
      trainingPaceHoursPerWeek: value.trainingPaceHoursPerWeek,
    });
  };

  return (
    <OptionsList role="radiogroup" aria-label="Deadline options">
      <OptionCard $selected={value.type === 'none'} onClick={() => handleSelect('none')}>
        <OptionTopRow>
          <RadioDot
            $selected={value.type === 'none'}
            role="radio"
            aria-checked={value.type === 'none'}
            aria-label="No deadline"
          />
          <OptionTextBlock>
            <OptionLabel>No deadline</OptionLabel>
            <OptionSubtitle>Self-paced learning with no reminders</OptionSubtitle>
          </OptionTextBlock>
        </OptionTopRow>
      </OptionCard>

      {!hideRecommended && (
        <OptionCard
          $selected={value.type === 'recommended'}
          $disabled={disableRecommended}
          onClick={() => handleSelect('recommended')}
          aria-disabled={disableRecommended}
        >
          <OptionTopRow>
            <RadioDot
              $selected={value.type === 'recommended'}
              role="radio"
              aria-checked={value.type === 'recommended'}
              aria-label="Recommended deadline"
            />
            <OptionTextBlock>
              <OptionLabel>Recommended deadline</OptionLabel>
              <OptionSubtitle>
                {shouldShowBulkRecommendedSubtitle
                  ? 'Auto-calculated based on training pace'
                  : 'Auto-calculated based on training pace and current progress'}
              </OptionSubtitle>
            </OptionTextBlock>

            {value.type === 'recommended' && !disableRecommended && (
              <TrainingPaceControl onClick={(event) => event.stopPropagation()}>
                <PaceLabel>Training pace:</PaceLabel>
                <TrainingPacePill>
                  <PaceValue>{pace}</PaceValue>
                  <PaceButtonsCol>
                    <PaceButton
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePaceChange(1);
                      }}
                      aria-label="Increase training pace"
                    >
                      <Icon
                        icon="ChevronUpPurple"
                        aria-hidden="true"
                        focusable="false"
                        style={{ width: 'auto', height: 'auto' }}
                      />
                    </PaceButton>
                    <PaceButton
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePaceChange(-1);
                      }}
                      aria-label="Decrease training pace"
                    >
                      <Icon
                        icon="ChevronDownPurple"
                        aria-hidden="true"
                        focusable="false"
                        style={{ width: 'auto', height: 'auto' }}
                      />
                    </PaceButton>
                  </PaceButtonsCol>
                  <PaceUnit>hours/week</PaceUnit>
                </TrainingPacePill>
              </TrainingPaceControl>
            )}
          </OptionTopRow>

          {value.type === 'recommended' &&
            !disableRecommended &&
            recommendedPreviewMode === 'weeks' && (
              <RecommendedExpanded>
                <DeadlineResultBox>
                  <DeadlineResultDate>
                    Deadline: {weeksToComplete} {weeksToComplete === 1 ? 'week' : 'weeks'} to
                    complete
                  </DeadlineResultDate>
                </DeadlineResultBox>
              </RecommendedExpanded>
            )}
          {value.type === 'recommended' &&
            !disableRecommended &&
            recommendedPreviewMode === 'date' && (
              <RecommendedExpanded>
                <DeadlineResultBox>
                  <DeadlineResultDate>
                    Deadline: {format(parseISO(value.date ?? recommendedDate), 'MMM d, yyyy')}
                  </DeadlineResultDate>
                  <DeadlineResultMeta>
                    <span>{recommendedDaysNeeded} days</span> remaining based on{' '}
                    <span>{pace} hours/week</span>. <span>{formatDuration(remainingSeconds)}</span>{' '}
                    left to complete.
                  </DeadlineResultMeta>
                </DeadlineResultBox>
              </RecommendedExpanded>
            )}
          {disableRecommended && (
            <RecommendedLoadingNotice>
              <InlineSpinnerContainer data-testid="recommended-deadline-refresh-spinner">
                <Spinner active position="absolute" top="50%" left="50%" size="14" />
              </InlineSpinnerContainer>
              Calculating the recommended deadline based on the updated playlist duration...
            </RecommendedLoadingNotice>
          )}
        </OptionCard>
      )}

      {!hideCustom && (
        <OptionCard
          $selected={value.type === 'custom'}
          onClick={() => {
            if (value.type !== 'custom') {
              handleSelect('custom');
            }
          }}
        >
          <OptionTopRow>
            <RadioDot
              $selected={value.type === 'custom'}
              role="radio"
              aria-checked={value.type === 'custom'}
              aria-label="Custom deadline"
            />
            <OptionTextBlock>
              <CustomDateHeader>
                <CustomDateSummary>
                  <OptionLabel>Custom deadline</OptionLabel>
                  <OptionSubtitle>Pick a specific completion date</OptionSubtitle>
                </CustomDateSummary>

                {value.type === 'custom' && (
                  <StyledDatePicker
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <DatePicker
                      value={value.date ? parseISO(value.date) : null}
                      onChange={handleDateChange}
                      disabledDate={disablePastDates}
                      format="MMM d, yyyy"
                      placeholder="Select a date"
                      suffixIcon={<Icon icon="CalendarTransparent" size="medium" color="#7965C6" />}
                      style={{ width: '100%' }}
                    />
                  </StyledDatePicker>
                )}
              </CustomDateHeader>
            </OptionTextBlock>
          </OptionTopRow>

          {value.type === 'custom' && (isPastDate(value.date) || customTrainingPaceNeeded != null) && (
            <CustomDateExpanded>
              {isPastDate(value.date) ? (
                <PastDateWarning>Past dates are not allowed for deadlines.</PastDateWarning>
              ) : (
                <CustomDateMeta>
                  <Icon icon="Info" fill="#A793F6" size="small" aria-hidden="true" />
                  <span>
                    <strong>
                      {formatHoursPerWeek(customTrainingPaceNeeded ?? 0)} hours of training/week
                    </strong>{' '}
                    needed to complete by date selected.
                  </span>
                </CustomDateMeta>
              )}
            </CustomDateExpanded>
          )}
        </OptionCard>
      )}
    </OptionsList>
  );
};

export default DeadlineSelector;
